import Hls from 'hls.js';
import type { RadioStation } from '../types/radio';
import {
  buildStreamCandidates,
  classifyFailure,
  httpsFirst,
  isHlsUrl,
  isPlaylistUrl,
  parsePlaylist,
  type UnplayableReason,
} from '../utils/streamUrl';
import { logger } from '../utils/logger';

/**
 * The single owner of playback for the whole app.
 *
 * This is a module singleton rather than per-component state on purpose. The UI
 * swaps between DesktopPlayerBar and MobilePlayerBar on a media query, and the
 * old code gave each one its own `new Audio()` — crossing the breakpoint mid-song
 * left two elements playing. One engine, one element, both bars just render it.
 *
 * It deliberately does NOT import the zustand store: sleepTimerService and the
 * store itself both need to call into the engine, and a store import here would
 * make that circular. State flows outward through `subscribe`.
 */

export type ConnectionQuality = 'good' | 'fair' | 'poor';

export interface AudioEngineState {
  isPlaying: boolean;
  isBuffering: boolean;
  connectionQuality: ConnectionQuality;
  streamError: boolean;
  unplayableReason: UnplayableReason | null;
  currentUrl: string | null;
}

const HLS_CONFIG = {
  enableWorker: true,
  startLevel: -1,
  debug: false,
  autoStartLoad: true,
  maxBufferLength: 30,
  maxMaxBufferLength: 600,
  maxBufferSize: 60 * 1000 * 1000,
  maxBufferHole: 0.5,
  lowLatencyMode: true,
  /**
   * An https manifest can still name its variant playlists and segments over
   * http, and those requests are blocked as mixed content on an https page —
   * which is what "requested an insecure XMLHttpRequest endpoint" in the console
   * was. The manifest loads, then everything under it fails.
   *
   * The host is serving the same files on https in every case seen so far, so
   * the scheme is upgraded before the request goes out. If a server genuinely
   * has no https, that request fails either way; it was already being blocked.
   */
  xhrSetup: (xhr: XMLHttpRequest, url: string) => {
    if (url.startsWith('http://') && typeof window !== 'undefined' && window.location.protocol === 'https:') {
      xhr.open('GET', `https://${url.slice('http://'.length)}`, true);
    }
  },
};

/**
 * How many times to retry a URL that has never produced sound. Once it has, the
 * retries are unbounded — see `reconnect`.
 *
 * This used to be 3 attempts on a 1.5x backoff from 2s, applied to every case:
 * a recovery window of 9.5 seconds even for a stream that had been playing for an
 * hour. A phone loses the network for longer than that constantly — a tunnel, a
 * lift, a wifi-to-mobile handoff, the screen locking — and past 9.5s the engine
 * gave up for good.
 *
 * The delay is capped so an unbounded retry settles into a steady poll rather
 * than growing without limit.
 */
const MAX_RECONNECTS = 10;
const RECONNECT_BASE_DELAY = 2000;
const RECONNECT_MAX_DELAY = 15000;
/**
 * Guard against trading pause-and-play with the operating system. If more than
 * this many system pauses arrive inside the window, playback is left stopped.
 */
const MAX_SYSTEM_RESUMES = 4;
const SYSTEM_RESUME_WINDOW = 20000;
/** No progress for this long while we intend to be playing => reconnect. */
const STALL_TIMEOUT = 15000;
/** Playlist files are tiny; don't let a dead host hold up the ladder. */
const PLAYLIST_TIMEOUT = 6000;

const INITIAL_STATE: AudioEngineState = {
  isPlaying: false,
  isBuffering: false,
  connectionQuality: 'poor',
  streamError: false,
  unplayableReason: null,
  currentUrl: null,
};

class AudioEngine {
  private audio: HTMLAudioElement | null = null;
  private hls: Hls | null = null;

  private state: AudioEngineState = { ...INITIAL_STATE };
  private listeners = new Set<(state: AudioEngineState) => void>();
  private tickListeners = new Set<() => void>();

  private station: RadioStation | null = null;
  private candidates: string[] = [];
  private candidateIndex = 0;

  /**
   * Monotonic id bumped on every new station load *and* every candidate attempt.
   * Async callbacks capture it and bail if it no longer matches.
   *
   * It has to cover both cases. Switching stations must invalidate the old
   * station's callbacks (otherwise a dying station steals playback back — the
   * "reverted to the older station" bug). But moving to the next candidate must
   * invalidate the previous candidate's callbacks too: setting src and calling
   * load() rejects the outstanding play() promise with AbortError, and that stale
   * rejection would otherwise advance the ladder a second time and skip a URL
   * that actually works.
   */
  private attemptId = 0;

  /** Set once a candidate actually produces sound, so we reconnect it rather than skipping it. */
  private connected = false;

  /**
   * Whether this station has ever been audible on the current URL.
   *
   * Distinct from `connected`, which goes false the moment a drop is detected.
   * This one stays true for the life of the station, and it is what tells the
   * difference between "this URL never worked" — where giving up and trying
   * another candidate is right — and "this URL worked and the network went away",
   * where the only correct thing to do is keep trying.
   */
  private everConnected = false;
  private reconnects = 0;

  /**
   * Whether we are *trying* to play, as opposed to whether audio is currently
   * flowing. These must be tracked separately.
   *
   * Setting src and calling load() makes the element fire `pause`. Without this
   * flag that pause was published as isPlaying:false, the store pushed it back in
   * as a play/pause command, and the effect paused a stream that was still
   * loading — which play() then undid, over and over. It only showed up after a
   * failed station, because that is the one case where the engine sits at
   * isPlaying:false while the store is being set to true, so the very first patch
   * of the next load published a contradiction.
   */
  private intent = false;

  /**
   * True from the moment a candidate starts opening until it is audible or has
   * failed. `resume()` must not touch the element during this window: the load
   * calls play() itself at the right moment, and for HLS calling play() before
   * MANIFEST_PARSED rejects with NotSupportedError, which the error path would
   * misread as a dead URL and skip a candidate that was about to work.
   */
  private loading = false;

  /** Window/document listeners are registered once, not per station. */
  private recoveryWired = false;

  private systemResumes = 0;
  private lastSystemResume = 0;

  private stallTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private bufferingTimer: ReturnType<typeof setTimeout> | null = null;

  private volume = 0.8;
  private muted = false;

  // ---------------------------------------------------------------- public API

  subscribe(listener: (state: AudioEngineState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Called on every `timeupdate`/`progress` while audio is flowing (~4x/sec).
   *
   * These come from the media pipeline, which keeps running when the tab is
   * hidden and the screen is off — unlike setInterval, which browsers throttle to
   * roughly once a minute in background tabs and suspend outright on mobile. The
   * sleep timer rides on this so it still fires when you've actually fallen asleep.
   */
  onTick(listener: () => void): () => void {
    this.tickListeners.add(listener);
    return () => {
      this.tickListeners.delete(listener);
    };
  }

  getState(): AudioEngineState {
    return this.state;
  }

  /** The live element, for anything that needs to read it (e.g. media session). */
  getElement(): HTMLAudioElement | null {
    return this.audio;
  }

  /**
   * Point the engine at a station without opening the stream. Used when a station
   * is selected but playback shouldn't start yet (restored session, expired sleep
   * timer) so a later `resume()` opens the right stream rather than the previous one.
   */
  prepare(station: RadioStation): void {
    this.stop();
    this.station = station;
    this.candidates = buildStreamCandidates(station);
    this.candidateIndex = 0;
    this.connected = false;
    this.everConnected = false;
    this.reconnects = 0;
  }

  playStation(station: RadioStation): void {
    const element = this.ensureElement();
    if (!element) return;

    this.station = station;
    this.candidates = buildStreamCandidates(station);
    this.candidateIndex = 0;
    this.connected = false;
    this.everConnected = false;
    this.reconnects = 0;
    this.intent = true;
    this.systemResumes = 0;
    this.publishNowPlaying(station);

    const token = this.beginLoad();

    if (this.candidates.length === 0) {
      logger.error(`No usable stream URL for station: ${station.name}`);
      this.fail();
      return;
    }

    // Publish the intent up front so the store never sees a stale isPlaying:false
    // for a station we are actively opening. Buffering state carries the "not
    // audible yet" nuance.
    this.patch({ isPlaying: true, isBuffering: true });

    logger.info(
      `Playing ${station.name} — ${this.candidates.length} candidate URL(s) to try`
    );
    this.tryCandidate(token);
  }

  /** Resume the current stream. Re-opens from scratch if the source was torn down. */
  resume(): void {
    const element = this.ensureElement();
    if (!element || !this.station) return;
    // Already running — the store echoing its own state back at us.
    if (!element.paused) return;
    // A station that already exhausted every candidate shouldn't retry on echo.
    if (this.state.streamError) return;

    this.intent = true;
    // A load already in progress will start playback itself when ready.
    if (this.loading) return;

    if (!element.getAttribute('src') && !this.hls) {
      this.playStation(this.station);
      return;
    }

    const token = this.attemptId;
    void element.play().catch((error) => {
      if (token !== this.attemptId) return;
      if (error?.name === 'NotAllowedError') {
        this.patch({ isPlaying: false });
        return;
      }
      if (error?.name === 'AbortError') return;
      logger.error('Resume failed:', error);
      this.recoverOrAdvance(token, 'Resume failed');
    });
  }

  pause(): void {
    if (this.audio && this.audio.paused && !this.state.isPlaying) return;
    this.intent = false;
    this.loading = false;
    this.clearTimers();
    if (this.audio) this.audio.pause();
    this.patch({ isPlaying: false, isBuffering: false });
  }

  /** Hard stop: pause and release the source. Used by the sleep timer. */
  stop(): void {
    this.intent = false;
    this.loading = false;
    this.beginLoad();
    this.teardownHls();
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
    }
    this.patch({
      isPlaying: false,
      isBuffering: false,
      connectionQuality: 'poor',
      currentUrl: null,
    });
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, Number(volume) || 0));
    if (this.audio) this.audio.volume = this.muted ? 0 : this.volume;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.audio) this.audio.volume = muted ? 0 : this.volume;
  }

  // ------------------------------------------------------------ candidate walk

  private beginLoad(): number {
    this.clearTimers();
    this.attemptId += 1;
    this.patch({ streamError: false, unplayableReason: null });
    return this.attemptId;
  }

  private tryCandidate(token: number): void {
    if (token !== this.attemptId) return;

    // Claim a fresh id so the outgoing candidate's pending promises go stale.
    const attempt = ++this.attemptId;
    const element = this.audio;
    const url = this.candidates[this.candidateIndex];
    if (!element || !url) {
      this.fail();
      return;
    }

    this.teardownHls();
    this.connected = false;
    this.loading = true;
    this.setBuffering(true);
    this.patch({ currentUrl: url, connectionQuality: 'poor' });

    logger.info(`Trying candidate ${this.candidateIndex + 1}/${this.candidates.length}: ${url}`);

    if (isPlaylistUrl(url)) {
      void this.resolvePlaylist(url, attempt);
    } else if (isHlsUrl(url, this.station)) {
      this.startHls(url, attempt);
    } else {
      this.startNative(url, attempt);
    }
  }

  /**
   * A `.pls`/`.m3u` is a pointer file, not audio. Fetch it, splice whatever
   * stream URLs it names into the candidate list, and continue down the ladder.
   *
   * The fetch is cross-origin and plenty of stations send no CORS headers, so
   * failure is expected and simply falls through to the next candidate — exactly
   * what happened before, just with a chance of succeeding first.
   */
  private async resolvePlaylist(url: string, attempt: number): Promise<void> {
    logger.info(`Resolving playlist: ${url}`);

    // Silence whatever was playing before doing anything else. Ordinary
    // candidates cut the old stream implicitly by assigning src, but a playlist
    // touches the element only after its fetch returns — so without this the
    // previous station stays audible for the length of the request.
    if (this.audio && !this.audio.paused) {
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
    }

    let body: string | null = null;
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), PLAYLIST_TIMEOUT);
      const response = await fetch(url, { signal: controller.signal, redirect: 'follow' })
        .finally(() => clearTimeout(timeout));
      if (response.ok) body = await response.text();
      else logger.warning(`Playlist fetch returned ${response.status}`);
    } catch (error) {
      // Almost always CORS. Nothing we can do from the page.
      logger.warning(`Playlist fetch failed (likely CORS): ${String(error).slice(0, 120)}`);
    }

    if (attempt !== this.attemptId) return;

    const entries = body ? parsePlaylist(body, url) : [];
    if (entries.length === 0) {
      logger.warning('Playlist yielded no stream URLs');
      this.advance(attempt);
      return;
    }

    const expanded = entries
      .flatMap((entry) => httpsFirst(entry))
      .filter((candidate) => !this.candidates.includes(candidate));

    if (expanded.length === 0) {
      this.advance(attempt);
      return;
    }

    logger.info(`Playlist resolved to ${expanded.length} stream URL(s)`);
    this.candidates.splice(this.candidateIndex + 1, 0, ...expanded);
    this.advance(attempt);
  }

  private startNative(url: string, token: number): void {
    const element = this.audio;
    if (!element) return;

    element.src = url;
    element.load();
    void element.play().catch((error) => {
      if (token !== this.attemptId) return;
      // NotAllowedError means autoplay policy, not a broken stream — the user
      // simply hasn't interacted yet. Skipping candidates would be wrong here.
      if (error?.name === 'NotAllowedError') {
        logger.warning('Playback blocked pending user interaction');
        this.setBuffering(false);
        this.patch({ isPlaying: false });
        return;
      }
      // AbortError means we replaced this request ourselves; it says nothing
      // about whether the URL works.
      if (error?.name === 'AbortError') return;
      logger.error(`Candidate failed (${url}):`, error);
      this.recoverOrAdvance(token, 'Playback failed');
    });
  }

  private startHls(url: string, token: number): void {
    const element = this.audio;
    if (!element) return;

    // Safari plays HLS natively and does it better than hls.js can.
    if (!Hls.isSupported()) {
      if (element.canPlayType('application/vnd.apple.mpegurl')) {
        this.startNative(url, token);
      } else {
        logger.error('HLS unsupported in this browser');
        this.advance(token);
      }
      return;
    }

    // A reconnect comes straight back here without going through tryCandidate,
    // so the previous instance has to be released or two of them end up attached
    // to the same element, both fetching and both writing to the MediaSource.
    this.teardownHls();

    const hls = new Hls(HLS_CONFIG);
    this.hls = hls;

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      if (token !== this.attemptId) return;
      void element.play().catch((error) => {
        if (token !== this.attemptId) return;
        if (error?.name === 'NotAllowedError') {
          this.setBuffering(false);
          this.patch({ isPlaying: false });
          return;
        }
        logger.error('HLS play failed:', error);
        this.recoverOrAdvance(token, 'HLS play failed');
      });
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (token !== this.attemptId || !data.fatal) return;

      // A fatal media error is often recoverable in place.
      if (
        data.type === Hls.ErrorTypes.MEDIA_ERROR &&
        (this.everConnected || this.reconnects < MAX_RECONNECTS)
      ) {
        this.reconnects += 1;
        logger.warning(`HLS media error, recovering (attempt ${this.reconnects})`);
        hls.recoverMediaError();
        return;
      }

      // A stream that has already been audible does not stop being the right URL
      // because the network dropped. Losing signal on a phone surfaces here as a
      // fatal networkError, and this used to go straight to the next candidate
      // and then to giving up — which is how an HLS station (every BBC one) ended
      // a listening session for good. Reconnect instead, exactly as the non-HLS
      // path does.
      logger.error(`Fatal HLS error (${data.type}) on ${url}`);
      this.recoverOrAdvance(token, `HLS ${data.type}`);
    });

    hls.loadSource(url);
    hls.attachMedia(element);
  }

  /**
   * What to do when an attempt fails.
   *
   * Moving to the next candidate is right only while the current URL has never
   * worked. Once it has been audible, a failure means the network went away, and
   * walking down the candidate list ends at `fail()` — a stopped player that only
   * a manual press of play will restart. Every path that gives up has to make
   * this distinction, not just the obvious one, or the listener still loses the
   * stream through whichever path was missed.
   */
  private recoverOrAdvance(token: number, why: string): void {
    if (token !== this.attemptId) return;
    if (this.everConnected) {
      logger.warning(`${why} on a stream that was playing — reconnecting`);
      this.reconnect(token);
      return;
    }
    this.advance(token);
  }

  /** Move to the next candidate URL, or give up if we've run out. */
  private advance(token: number): void {
    if (token !== this.attemptId) return;

    this.candidateIndex += 1;
    if (this.candidateIndex >= this.candidates.length) {
      this.fail();
      return;
    }
    this.tryCandidate(this.attemptId);
  }

  private fail(): void {
    this.intent = false;
    this.loading = false;
    this.clearTimers();
    this.teardownHls();
    if (this.audio) this.audio.pause();

    const reason = classifyFailure(this.station);
    logger.error(
      `All candidates exhausted for ${this.station?.name ?? 'station'} (${reason})`
    );

    // Note what is deliberately NOT done here: the current station is left in
    // place and no other station is started. A failed station must never hand
    // playback to a different one.
    this.patch({
      isPlaying: false,
      isBuffering: false,
      connectionQuality: 'poor',
      streamError: true,
      unplayableReason: reason,
    });
  }

  // ----------------------------------------------------------- reconnect logic

  /** A stream that was working and dropped: re-open the same URL with backoff. */
  private reconnect(token: number): void {
    if (token !== this.attemptId) return;

    // A URL that has already produced sound is the right URL. If it goes quiet
    // the network broke, not the station, so keep trying it for as long as the
    // listener wants to hear it rather than walking off to a worse candidate and
    // then stopping altogether.
    //
    // The version before this engine retried forever too, which is why playback
    // always came back on its own. Replacing it with a three-attempt cap meant
    // any outage longer than 9.5 seconds ended the session permanently — the
    // listener had to reopen the app and press play. The cap now applies only
    // while we have never heard anything from this URL, which is the case where
    // moving on genuinely is the right move.
    //
    // Nothing runs away with this: `pause()` and every station change clear the
    // timers, and the delay settles at RECONNECT_MAX_DELAY.
    if (!this.everConnected && this.reconnects >= MAX_RECONNECTS) {
      logger.error('Max reconnect attempts reached');
      this.advance(token);
      return;
    }

    this.reconnects += 1;
    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(1.5, this.reconnects - 1),
      RECONNECT_MAX_DELAY
    );
    logger.info(`Reconnecting in ${Math.round(delay)}ms (${this.reconnects}/${MAX_RECONNECTS})`);

    this.setBuffering(true);
    this.clearTimer('reconnectTimer');
    this.reconnectTimer = setTimeout(() => {
      if (token !== this.attemptId) return;
      const url = this.candidates[this.candidateIndex];
      if (!url) return;
      if (isHlsUrl(url, this.station)) {
        this.startHls(url, token);
      } else {
        this.startNative(url, token);
      }
    }, delay);
  }

  /**
   * Watch for a stream that *was* flowing and went quiet.
   *
   * Only meaningful once audio has actually started. `waiting` and `stalled` also
   * fire while a candidate is still trying to open — a connection that hangs
   * emits them repeatedly — and arming on those meant the watchdog spent the
   * reconnect budget retrying a URL that had never worked, in parallel with the
   * candidate ladder trying to move past it. A dead station burned all three
   * attempts before the second candidate was even reached.
   *
   * An attempt that never connects is the ladder's job, not the watchdog's.
   */
  private armStallWatchdog(): void {
    this.clearTimer('stallTimer');
    if (!this.connected) return;
    this.stallTimer = setTimeout(() => {
      if (!this.connected || !this.state.isPlaying) return;
      logger.warning('No stream progress — reconnecting');
      this.reconnect(this.attemptId);
    }, STALL_TIMEOUT);
  }

  // ----------------------------------------------------------- element wiring

  /**
   * Come back on our own when the conditions that broke playback go away.
   *
   * The reported symptom was a stream stopping and staying stopped until the
   * listener reopened the app and pressed play. Waiting on a timer alone cannot
   * fix that: on a phone the timers are throttled while the tab is in the
   * background, so the retries that should have happened during the outage are
   * either late or never run, and the moment the listener actually returns is
   * exactly the moment nothing is scheduled.
   *
   * So the two events that mean "it is worth trying again right now" are used
   * directly. Both reset the attempt counter, because coming back online is new
   * information, not another failure.
   */
  private wireRecovery(): void {
    if (this.recoveryWired || typeof window === 'undefined') return;
    this.recoveryWired = true;

    const retryNow = (why: string) => {
      // Only when we are meant to be playing and are not. A paused player, or one
      // already streaming happily, must be left alone.
      if (!this.intent || this.connected || this.loading) return;
      if (!this.station || this.candidates.length === 0) return;
      logger.info(`${why} — retrying the stream`);
      this.reconnects = 0;
      this.clearTimer('reconnectTimer');
      this.reconnect(this.attemptId);
    };

    window.addEventListener('online', () => retryNow('Back online'));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') retryNow('App back in view');
    });
  }

  /**
   * Pick playback back up after something outside the app stopped it.
   *
   * Guarded against a fight: if the system keeps pausing us — audio focus given
   * to another app for good, say — this backs off instead of trading pause and
   * play with it forever, and leaves the UI honest about not playing.
   */
  private resumeAfterSystemPause(): void {
    const element = this.audio;
    if (!element || !this.intent) return;

    const now = Date.now();
    if (now - this.lastSystemResume > SYSTEM_RESUME_WINDOW) this.systemResumes = 0;
    this.lastSystemResume = now;
    this.systemResumes += 1;

    if (this.systemResumes > MAX_SYSTEM_RESUMES) {
      logger.warning('System keeps pausing playback — leaving it stopped');
      this.intent = false;
      this.patch({ isPlaying: false, isBuffering: false });
      return;
    }

    logger.warning('Playback paused from outside the app — resuming');
    const token = this.attemptId;
    void element.play().catch((error) => {
      if (token !== this.attemptId || !this.intent) return;
      if (error?.name === 'AbortError') return;
      if (error?.name === 'NotAllowedError') {
        // The browser wants a gesture. Pressing play on the notification is one.
        this.patch({ isPlaying: false });
        return;
      }
      // Could not pick it up in place, so treat it as a drop and use the normal
      // recovery, which knows about backoff and about never abandoning a URL
      // that has already worked.
      this.reconnect(token);
    });
  }

  /**
   * Hand the OS proper transport controls.
   *
   * Without this, the lock screen and notification controls are whatever Chrome
   * infers, and — the part that matters here — a press of the notification's
   * pause button reaches the element directly, indistinguishable from the system
   * pausing us. These handlers route it through the same path as the in-app
   * button, so `intent` is right and the pause listener above can trust it.
   */
  private wireMediaSession(): void {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const session = navigator.mediaSession;
    try {
      session.setActionHandler('play', () => this.resume());
      session.setActionHandler('pause', () => this.pause());
      session.setActionHandler('stop', () => this.stop());
    } catch {
      // An older browser may not know every action; the ones it took still work.
    }
  }

  /** Show the station on the lock screen rather than a bare "audio playing". */
  private publishNowPlaying(station: RadioStation): void {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const MetadataCtor = (window as any).MediaMetadata;
    if (!MetadataCtor) return;
    try {
      navigator.mediaSession.metadata = new MetadataCtor({
        title: station.name,
        artist: station.country || 'Radio',
        album: 'AuraFY',
        artwork: station.favicon ? [{ src: station.favicon }] : [],
      });
    } catch {
      // Metadata is decoration; never let it interrupt playback.
    }
  }

  private ensureElement(): HTMLAudioElement | null {
    if (typeof window === 'undefined') return null;
    if (this.audio) return this.audio;

    const element = new Audio();
    element.preload = 'none';
    element.volume = this.muted ? 0 : this.volume;
    this.wireRecovery();
    this.wireMediaSession();
    // `crossOrigin` is intentionally left unset. Setting it to 'anonymous' turns
    // every request into a CORS request, and Icecast/Shoutcast servers send no
    // Access-Control-Allow-Origin — it would break almost every station.

    // Registered exactly once, and every handler reads live fields off `this`
    // rather than closing over React state. The old code assigned these inside a
    // useEffect with an empty dep array, so they captured the first render's
    // station forever.
    element.addEventListener('playing', () => {
      this.connected = true;
      this.everConnected = true;
      this.loading = false;
      this.reconnects = 0;
      this.setBuffering(false);
      this.patch({ isPlaying: true, connectionQuality: 'good', streamError: false });
      this.armStallWatchdog();
    });

    element.addEventListener('play', () => {
      this.patch({ isPlaying: true });
    });

    element.addEventListener('pause', () => {
      // Setting src / calling load() fires `pause` even though we are opening a
      // stream, not stopping one. That window is exactly `loading`.
      if (this.loading) return;

      if (this.intent) {
        // Nobody asked for this. Android pauses media for doze, battery saving
        // and audio focus loss — a call, a notification, another app taking the
        // output — and it arrives as a plain pause with no error, no `ended` and
        // no stall, so nothing else in here notices. Ignoring it left the element
        // parked with the UI still showing "playing", and the only way back was
        // pressing play on the notification.
        //
        // Reachable only because the media session handlers below clear `intent`
        // for a pause the listener actually asked for. Without them this would
        // fight the notification's own pause button.
        this.resumeAfterSystemPause();
        return;
      }

      this.clearTimer('stallTimer');
      this.patch({ isPlaying: false, isBuffering: false });
    });

    // A live stream that ends has dropped; reconnect rather than sitting silent
    // with the UI still showing "playing". Capped by the reconnect counter.
    element.addEventListener('ended', () => {
      if (!this.intent) return;
      logger.warning('Stream ended unexpectedly — reconnecting');
      this.reconnect(this.attemptId);
    });

    element.addEventListener('waiting', () => {
      this.setBuffering(true);
      this.patch({ connectionQuality: 'fair' });
      this.armStallWatchdog();
    });

    element.addEventListener('progress', () => {
      this.armStallWatchdog();
      this.emitTick();
    });
    element.addEventListener('timeupdate', () => {
      this.armStallWatchdog();
      this.emitTick();
    });

    // `stalled` alone must not trigger a reload — it fires routinely on healthy
    // live streams. The watchdog decides, based on actual lack of progress.
    element.addEventListener('stalled', () => {
      this.patch({ connectionQuality: 'poor' });
      this.armStallWatchdog();
    });

    element.addEventListener('error', () => {
      const token = this.attemptId;
      const mediaError = element.error;
      logger.error(
        `Audio error (code ${mediaError?.code ?? 'unknown'}) on ${this.state.currentUrl}`
      );
      // While hls.js is attached it owns the element and reports real problems
      // through Hls.Events.ERROR. Native errors here are noise — in particular,
      // destroying an hls.js instance clears the element's source, and the
      // resulting MEDIA_ERR_SRC_NOT_SUPPORTED is dispatched as a queued task, so
      // it arrives after the *next* candidate has started and would otherwise be
      // blamed on it. That killed every HLS station on its second candidate.
      if (this.hls) return;
      // Same story for an emptied source outside HLS.
      if (!element.getAttribute('src')) return;

      if (this.connected) {
        this.reconnect(token);
      } else {
        this.advance(token);
      }
    });

    this.audio = element;
    return element;
  }

  private teardownHls(): void {
    if (!this.hls) return;
    try {
      this.hls.destroy();
    } catch (error) {
      logger.warning(`Failed to destroy HLS instance: ${String(error)}`);
    }
    this.hls = null;
  }

  // ------------------------------------------------------------------ plumbing

  /** Debounced so a fast-connecting stream doesn't flash a spinner. */
  private setBuffering(buffering: boolean): void {
    this.clearTimer('bufferingTimer');
    if (buffering) {
      this.bufferingTimer = setTimeout(() => this.patch({ isBuffering: true }), 300);
    } else {
      this.patch({ isBuffering: false });
    }
  }

  private clearTimer(name: 'stallTimer' | 'reconnectTimer' | 'bufferingTimer'): void {
    const timer = this[name];
    if (timer) {
      clearTimeout(timer);
      this[name] = null;
    }
  }

  private clearTimers(): void {
    this.clearTimer('stallTimer');
    this.clearTimer('reconnectTimer');
    this.clearTimer('bufferingTimer');
  }

  private emitTick(): void {
    this.tickListeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        logger.error('Tick listener failed:', error);
      }
    });
  }

  private patch(next: Partial<AudioEngineState>): void {
    const merged = { ...this.state, ...next };
    const unchanged = (Object.keys(next) as (keyof AudioEngineState)[]).every(
      (key) => this.state[key] === merged[key]
    );
    if (unchanged) return;

    this.state = merged;
    this.listeners.forEach((listener) => listener(this.state));
  }
}

export const audioEngine = new AudioEngine();
